// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// T11 mandatory 13-step onboarding + profile management. Steps 1-6/7-12
// copy is adapted from docs/product/main.html's onboarding modal (kept in
// the same informal, direct voice); step13 (health conditions, ADR-013)
// and the profile/hard-constraint copy are written fresh since main.html
// predates that decision.
export const onboardingEn = {
    shell: {
        tuneLaterHint: "Taste, budget and kitchen? Amy picks those up as you go — everything stays editable in Preferences.",
        stepOf: "Step {{current}} of {{total}}",
        back: "Back",
        continue: "Continue",
        finish: "Create my first plan",
        saving: "Saving…",
        missingRequired: "Almost there — a required field (marked *) still needs an answer.",
        missingRequiredField: "Almost there — {{field}} still needs an answer.",
        saveErrorTitle: "We couldn't save that answer",
        saveErrorBody: "Check your connection and try again. Nothing else on this step was lost.",
        resumeNotice: "Picking up where you left off.",
        languageSwitchNotice: "Your answers are kept when you switch language.",
        knowsTitle: "What BiteBalance knows so far",
        hardLimit: "Hard",
        knownGoal: "Goal",
        knownTarget: "Daily target",
        knownActivity: "Daily activity",
        knownDiet: "Diet",
        knownAllergens: "Allergens",
        knownIntolerances: "Intolerances",
        knownHealth: "Health",
        knownWeeknights: "Weeknights",
        knownSnacks: "Snacks",
        knownNone: "None",
    },
    finishing: {
        title: "Your plan is being created…",
        body: "Amy is putting together your first week of meals. This usually takes less than a minute.",
        failedTitle: "We couldn't create your plan just now",
        failedBody: "Your answers are saved. Continue to the app — you can start the plan again from your Today page, and nothing you entered is lost.",
        timedOutTitle: "Still working on it",
        timedOutBody: "Your plan is taking longer than usual to generate. You can keep waiting here, or continue now and check back on Today shortly — your plan will appear as soon as it's ready.",
        continueAnyway: "Continue anyway",
        errorDetail: "Technical detail: {{detail}}",
        errorTitle: "We couldn't start your plan",
        errorBody: "Something went wrong while starting your first plan. Your answers are saved, so it's safe to try again — or continue now and generate a plan later.",
        eyebrow: "Your week is taking shape",
        stageSaving: "Saving your answers",
        stagePlanning: "Putting your first week together",
        stageReady: "Plan ready",
        stageDone: "done",
        stageNow: "in progress",
        stageTodo: "pending",
        stageFailed: "not completed",
    },
    ineligible: {
        title: "Not available for you yet",
        ageBody: "BiteBalance is currently available to adults (18+) living in Germany.",
        edBody: "To keep BiteBalance safe, we can't offer meal planning to people with a history of an eating disorder yet. Please talk to a doctor or a trusted professional about your nutrition — we'd rather be honest with you than plan something unsafe.",
        contactCta: "Contact us for future updates",
        backToStart: "Back to start",
    },
    correction: {
        belowFloorTitle: "That target is below a safe minimum",
        belowFloorBody: "A target of {{target}} kcal/day is below the safe minimum of {{floor}} kcal/day for your profile. Choose the safe minimum instead, or adjust your inputs.",
        pregnancyGoalTitle: "Let's review your goal",
        pregnancyGoalBody: "Weight-loss targets aren't offered during pregnancy. Update your goal to maintain before continuing — BiteBalance never plans an unsafe target.",
        updateGoalCta: "Update my goal",
        acknowledgeFloorLabel: "I understand this is a calculated minimum and I'd like to use it.",
        useFloorCta: "Use the safe minimum",
        breastfeedingLossTitle: "A gentler weight-loss target while breastfeeding",
        breastfeedingLossBody: "We can offer a weight-loss goal while you're breastfeeding — and we manage it carefully for you:",
        breastfeedingLossPoint1Title: "Nursing calories are added back",
        breastfeedingLossPoint1Body: "Your target first adds back the extra calories breastfeeding burns, so your body never runs short.",
        breastfeedingLossPoint2Title: "Gentle by design",
        breastfeedingLossPoint2Body: "The deficit is capped at roughly 0.5 kg/week or less and never goes below a safe lactation minimum — this protects your milk supply.",
        breastfeedingLossPoint3Title: "Your care team has the final say",
        breastfeedingLossPoint3Body: "Everyone's situation is different — please confirm your target and goal with your doctor, midwife, or a lactation-aware dietitian.",
        acknowledgeBreastfeedingLossLabel: "I understand this is a capped, supply-protective estimate, not medical advice, and I'll confirm it with my doctor, midwife, or a lactation-aware dietitian.",
        breastfeedingLossContinueCta: "Continue with this goal",
    },
    step1: {
        eyebrow: "LANGUAGE · REQUIRED",
        title: "Choose your language",
        body: "The complete onboarding and app will use your choice.",
        de: "Deutsch",
        deSub: "Deutschland",
        en: "English",
        enSub: "Germany",
    },
    step2: {
        namesOptionalHint: "Optional — so Amy can greet you by name. You can add it anytime.",
        eyebrow: "ELIGIBILITY · REQUIRED",
        title: "Where are you starting?",
        body: "BiteBalance is currently available to adults living in Germany.",
        eligibleOption: "I am 18 or older and live in Germany",
        eligibleSub: "Continue with setup",
        ineligibleOption: "I am not eligible yet",
        ineligibleSub: "Contact us for future updates",
        firstNameLabel: "First name",
        familyNameLabel: "Family name",
        dobLabel: "Date of birth",
        dobHint: "BiteBalance is for adults (18+). Your date of birth stays private.",
        dobUnderage: "BiteBalance is available from age 18. This date of birth doesn't match the 18+ confirmation.",
        dobInvalid: "Enter a valid date of birth.",
    },
    step3: {
        eyebrow: "GOAL · REQUIRED",
        title: "What should food help you do?",
        body: "Choose your main goal. You can change it later.",
        maintain: "Maintain",
        maintainSub: "Keep weight stable",
        loss: "Gradual loss",
        lossSub: "Make steady progress",
        gain: "Gain",
        gainSub: "Support healthy weight gain",
        improve: "Improve my health",
        improveSub: "Feel better overall — balanced targets, no weight direction",
        ackLabel: "I understand BiteBalance will suggest a target for me to review next.",
    },
    step4: {
        eyebrow: "YOUR STARTING POINT · REQUIRED",
        title: "Tell us a little about you",
        body: "This helps BiteBalance suggest a daily nutrition target for you to review before planning.",
        sexLabel: "Sex (for the calculation)",
        sexFemale: "Female",
        sexMale: "Male",
        preferNotToAnswer: "Prefer not to answer",
        ageLabel: "Age",
        heightLabel: "Height (cm)",
        weightLabel: "Weight (kg)",
        targetWeightLabel: "Target weight (kg, optional)",
        activityLevelLabel: "Daily activity",
        activityLevelBody: "How much you move day to day — not your workouts, we'll ask about those next.",
        activitySedentary: "Mostly seated",
        activitySedentaryBody: "Desk job, mostly sitting, little walking during the day",
        activityLight: "Lightly active",
        activityLightBody: "Some walking or light chores, but mostly indoors",
        activityModerate: "On your feet often",
        activityModerateBody: "Retail, teaching, parenting, or a job that keeps you moving",
        activityHigh: "Physically demanding",
        activityHighBody: "Trades, delivery, hospitality — on your feet most of the day",
        sportsQuestion: "Do you do any sports or structured exercise?",
        sportsYes: "Yes",
        sportsNo: "Not right now",
        sportsAddCta: "Add a sport",
        sportsRemove: "Remove",
        sportsTypes: {
            strength: "Strength training",
            running: "Running",
            cycling: "Cycling",
            swimming: "Swimming",
            teamSports: "Team sports",
            yogaPilates: "Yoga / Pilates",
            walkingHiking: "Walking / hiking",
            other: "Other",
        },
        sportsSessionsLabel: "Sessions per week",
        sportsIntensityLabel: "Intensity",
        sportsIntensity: {
            low: "Low",
            moderate: "Moderate",
            high: "High",
        },
        sportsMinutesLabel: "Minutes per session (optional)",
        sportsMinutesPlaceholder: "e.g. 45",
        wearableQuestion: "Does your watch or fitness tracker show active calories burned?",
        wearableLabel: "Average daily active calories (optional)",
        wearableHint: 'From your device\'s "active energy" or "calories burned" stat, averaged over a week or two. When you enter this, we use it directly instead of the activity level above.',
        methodLabel: "Target calculation",
        methodCalculated: "Calculate my target for me",
        methodManual: "I prefer to enter my own target",
        manualLabel: "Daily energy target (kcal)",
        reviewTitle: "Your suggested target",
        reviewBody: "Based on what you entered, BiteBalance suggests {{kcal}} kcal/day for your goal.",
        floorNote: "BiteBalance never suggests or accepts a target below a safe minimum.",
        sourcesLink: "Sources & method",
        recommendedBadge: "Recommended",
        calculatedIntro: "We'll calculate a target for you based on what you shared above.",
        useCalculatedCta: "Use the calculated target instead",
        expectationTitle: "This is an estimate, not a guarantee",
        expectationBody: "Your nutrition target is an estimate based on the information you provide. Results differ between people and weight can fluctuate from day to day. BiteBalance helps you plan, track and adjust, but does not guarantee a particular change or completion date.",
    },
    step5: {
        eyebrow: "DIETARY STYLE · REQUIRED",
        title: "How do you like to eat?",
        body: "This becomes a rule for every plan.",
        omnivore: "Omnivore",
        pescatarian: "Pescatarian",
        vegetarian: "Vegetarian",
        vegan: "Vegan",
    },
    step6: {
        eyebrow: "ALLERGIES · REQUIRED",
        title: "Allergies or intolerances?",
        body: "Select every item that applies, or choose none.",
        allergens: {
            peanut: "Peanuts",
            treeNut: "Tree nuts",
            milk: "Milk (allergy)",
            gluten: "Gluten (cereals)",
            egg: "Eggs",
            fish: "Fish",
            shellfish: "Crustaceans (shrimp, crab)",
            soy: "Soy",
            sesame: "Sesame",
            celery: "Celery",
            mustard: "Mustard",
            sulphites: "Sulphur dioxide / sulphites",
            lupin: "Lupin",
            mollusc: "Molluscs (mussels, squid)",
        },
        intolerancesLabel: "Other intolerances (optional)",
        intolerancesPlaceholder: "e.g. histamine, FODMAPs",
        otherLabel: "Other allergy or intolerance",
        otherPlaceholder: "Type the ingredient or food",
        noneLabel: "None",
        hardNoteTitle: "Your safety comes first",
        hardNoteBody: "These items are permanently excluded from every plan we build for you, and can only be changed later with a confirmation step. Cross-contact ('may contain') is excluded where products declare it. Sourced allergen data can be incomplete — always check product labels and confirm each meal is safe for you before cooking or eating it. Because allergy information is health data, we only process it with your consent — given by completing this step — and you can review, change or delete it anytime under Food & health.",
        hardNoteWhy: "Why we ask: so Amy can rule out unsafe meals before they ever reach your plan, not just flag them afterward.",
        nutClarifierPeanuts: "You selected tree nuts. Peanuts are botanically different (a legume) — many nut allergies cover both. Add peanuts too?",
        nutClarifierTreeNuts: "You selected peanuts. Tree nuts (almonds, walnuts…) are a separate allergen class. Add tree nuts too?",
        nutClarifierAdd: "Yes, exclude both",
        nutClarifierKeep: "No, keep my selection",
        structuredIntoleranceRequired: "For lactose, use the \"Lactose intolerance\" option and choose how strict it is.",
        // ADR-080: a lactose intolerance is its own choice with a user-chosen level — never the milk allergy.
        lactoseChip: "Lactose intolerance",
        lactoseLevelTitle: "How strict is your lactose intolerance?",
        lactoseLevelAvoidAll: "I avoid all dairy",
        lactoseLevelAvoidAllSub: "Milk, cheese, yogurt, butter and cream stay out of every plan.",
        lactoseLevelFreeOk: "Lactose-free dairy is fine",
        lactoseLevelFreeOkSub: "Products labelled lactose-free stay in. Regular milk, cream, quark and most fresh cheese stay out — and so does any dairy we have no verified lactose data for.",
        lactoseLevelNote: "Your choice, not a diagnosis — change it any time under Food & health.",
        lactoseTextHint: "Use the \"Lactose intolerance\" option above — it has its own setting.",
    },
    step13: {
        eyebrow: "HEALTH · REQUIRED",
        title: "Any health conditions we should know about?",
        body: "This never unlocks a therapeutic or medical diet — it only lets BiteBalance apply the right food-safety rules and reminders for you. Select every item that applies, or choose none.",
        conditions: {
            pregnancy: "Pregnancy",
            breastfeeding: "Breastfeeding",
            type2_diabetes: "Type 2 diabetes",
            hypertension: "Hypertension (high blood pressure)",
            kidney_disease: "Chronic kidney disease",
            celiac: "Celiac disease",
            eating_disorder_history: "History of an eating disorder",
            other: "Other condition",
        },
        otherLabel: "Describe the other condition",
        otherPlaceholder: "Type the condition",
        medicationsLabel: "Current medications (optional, free text)",
        medicationsPlaceholder: "e.g. metformin, levothyroxine",
        noneLabel: "None",
        disclaimerTitle: "Please note:",
        disclaimerBody: "BiteBalance applies evidence-based food-safety rules for the conditions you select, but it does not create a medical or therapeutic diet plan. You can review, change or delete your answers anytime under Food & health.",
        consultDoctor: "Always check with a doctor or registered dietitian about your specific needs.",
    },
    step7: {
        eyebrow: "TASTE · REQUIRED",
        title: "What do you enjoy eating?",
        body: "Tell Amy what you love and what you never want in your plan. Choose none for anything that doesn't apply.",
        likesLabel: "Foods or cuisines you enjoy",
        likesPlaceholder: "e.g. Mediterranean, lentils, crispy vegetables",
        dislikesLabel: "Foods or combinations you dislike",
        dislikesPlaceholder: "e.g. soft mushrooms",
        neverEatLabel: "Add foods you don't want included in your meals.",
        neverEatPlaceholder: "e.g. pork, cilantro",
        dislikedCombinationsLabel: "Combinations you dislike",
        dislikedCombinationsPlaceholder: "e.g. never pineapple with savoury dishes",
        noneButton: "None of these apply",
    },
    step8: {
        eyebrow: "COOKING · REQUIRED",
        title: "How does cooking feel today?",
        body: "Skill and enjoyment change complexity, variety and instructions.",
        beginner: "Beginner",
        beginnerSub: "Simple techniques",
        intermediate: "Intermediate",
        intermediateSub: "Comfortable cooking",
        advanced: "Advanced",
        advancedSub: "More techniques",
        enjoymentHigh: "I like cooking",
        enjoymentLow: "Keep it minimal",
    },
    step9: {
        eyebrow: "TIME + KITCHEN · REQUIRED",
        title: "What fits a normal weekday?",
        body: "Amy can vary the limit later by meal or day.",
        perMealLabel: "Minutes per meal",
        perDayLabel: "Minutes per day",
        presetUpTo: "up to {{minutes}} min",
        equipmentLabel: "Equipment or limitations",
        equipmentPlaceholder: "e.g. one pan, oven, blender — or none",
    },
    step10: {
        eyebrow: "COOKING STYLE · REQUIRED",
        title: "How do you want to cook this week?",
        body: "Cook day by day, prepare several portions at once, or mix both.",
        daily: "Daily cooking",
        batch: "Batch cooking",
        hybrid: "Mix both",
        choose: "Choose",
        snackLabel: "Would you like snacks in your plan?",
        snackNone: "No snacks",
        snackMorning: "Morning snack",
        snackAfternoon: "Afternoon snack",
        snackBoth: "Both",
    },
    step11: {
        eyebrow: "BUDGET + STORE · REQUIRED",
        title: "What works for your weekly shop?",
        body: "Choose a budget, your usual store and how you prefer to shop.",
        under50: "Under €50/week",
        between: "€50–€80/week",
        noTarget: "No fixed target",
        retailerLabel: "Preferred retailer(s)",
        noPreference: "No preference",
        deliveryLabel: "Shopping style",
        pickup: "Pickup",
        delivery: "Delivery",
        either: "Either",
        none: "No preference",
        offerEmailsTitle: "💌 Never miss a deal at your stores",
        offerEmailsBody: "When your preferred stores run offers that match your plan or shopping list, we'll send you a heads-up — and Amy can fold the savings straight into your week.",
        offerEmailsToggle: "Yes, email me matching offers",
        offerEmailsFinePrint: "At most 1 email a week · off unless you opt in · unsubscribe with one click, anytime",
        offerEmailsSaved: "Saved — we'll keep an eye on your stores for you.",
    },
    step12: {
        eyebrow: "WHAT YOU HAVE · OPTIONAL",
        title: "Use what is already in your kitchen",
        body: "Optional — skip straight ahead if you like. Or jot a quick note in your own words and Amy will show you the items and amounts to confirm.",
        pantryLabel: "Food you already have",
        pantryPlaceholder: "I have six eggs, half a bag of spinach and about 300 g of rice.",
        nothingToAdd: "Nothing to add now",
        extractButton: "Check my items",
        candidatesTitle: "Check your items",
        candidatesBody: "Confirm the amounts before we add them to your kitchen.",
        confirmButton: "Confirm items",
        reviewTitle: "Review your answers",
        reviewHardTitle: "Hard rules (never crossed)",
        reviewSoftTitle: "Soft preferences (can be adjusted anytime)",
        reviewHardBody: "Allergens, intolerances, dietary style and food-safety exclusions from your health conditions.",
        reviewSoftBody: "Taste, cooking style, time, budget and everything else Amy can adapt around.",
        notMedicalTitle: "Not medical advice",
        notMedicalBody: "Amy is BiteBalance's AI assistant and does not replace a doctor or registered dietitian.",
        consentLabel: "I reviewed my answers and want BiteBalance to create my first plan.",
        createPlanButton: "Create my first plan",
    },
};
export const moreEn = {
    hub: {
        sectionYou: "About you",
        sectionFood: "Food & taste",
        sectionApp: "App & account",
        profileLink: "Personal Information",
        profileLinkSub: "Your name, photo, date of birth and contact details",
        preferencesLink: "Preferences",
        preferencesLinkSub: "Cooking, planning mode, snacks, activity, budget and notifications",
        goalsLink: "Goals",
        goalsLinkSub: "Your goal, calorie target, body data and time budget",
        constraintsLink: "Food & health",
        constraintsLinkSub: "Allergies, foods you avoid, and health conditions — always respected in your plans",
        memoryLink: "Memory",
        memoryLinkSub: "What Amy remembers about your taste, and pending suggestions",
        guidesLink: "Guide Center",
        guidesLinkSub: "Short honest reads: off-plan meals, the scale, and how Amy plans",
        sourcesLink: "Sources & method",
        sourcesLinkSub: "The published sources behind your calorie target, macros and safety minimums",
    },
    profile: {
        title: "Personal Information",
        back: "Back to Settings",
        personalTitle: "Personal information",
        preferencesTitle: "Preferences",
        firstNameLabel: "First name",
        familyNameLabel: "Family name",
        dobLabel: "Date of birth",
        emailLabel: "Email address",
        emailReadOnlyHint: "Your sign-in email can't be changed here.",
        phoneLabel: "Mobile phone (optional)",
        phoneOptionalHint: "e.g. +49 151 2345678",
        phoneInvalid: "Enter a valid phone number (6-15 digits, optional +).",
        photoAlt: "Profile photo",
        photoUpload: "Add photo",
        photoReplace: "Change photo",
        photoRemove: "Remove photo",
        photoUploading: "Uploading…",
        photoError: "We couldn't upload that photo. Please try again.",
        completeBannerTitle: "Complete your profile",
        completeBannerBody: "Add your name and date of birth to finish your personal record. Everything keeps working either way.",
        personalSaved: "Personal information saved.",
        personalSaveError: "We couldn't save your personal information. Please try again.",
        goalLabel: "Goal",
        calorieTargetLabel: "Daily calorie target",
        cookingLabel: "Cooking enjoyment & skill",
        planningModeLabel: "Cooking style",
        snackLabel: "Snacks in your plan",
        budgetLabel: "Weekly budget",
        retailersLabel: "Preferred retailers",
        saveButton: "Save changes",
        saved: "Saved.",
        saveError: "We couldn't save that. Please try again.",
        snackRegenTitle: "Snack preference saved",
        snackRegenBody: "Your current plan keeps its meals. The new snack setting applies the next time your plan is generated.",
        snackRegenCta: "Regenerate plan now",
        snackRegenStarted: "Plan regeneration started. Your new plan will appear shortly.",
        snackRegenError: "We couldn't start the regeneration. Please try again.",
        // ADR-029: honest consent copy — off by default, at most one email/week,
        // revocable anytime (Settings toggle or the unsubscribe link in every mail).
        notificationsTitle: "Notifications",
        offerEmailsLabel: "Offer emails",
        offerEmailsBody: "At most one email per week when current offers at your preferred stores match your plan or shopping list. Off by default — you can turn it off again here anytime, or via the unsubscribe link in every email.",
        offerEmailsSaved: "Notification setting saved.",
        offerEmailsError: "We couldn't save that setting. Please try again.",
        requiredFieldError: "This field is required and can't be left empty.",
        bodyTitle: "Body data",
        bodyIntro: "Sex, age, height and weight drive your daily calorie target. Changing them recomputes the target and updates your plan.",
        bodySaved: "Saved.",
        bodySaveError: "We couldn't save your body data. Please try again.",
        bodyInvalid: "Please check the highlighted values.",
        bodyTargetKept: "Your daily target is now {{to}} (was {{from}}). Your current week and shopping list stay exactly as planned — the new target applies from your next plan.",
        bodyTargetUnchanged: "Saved. Your daily calorie target is unchanged.",
        bodyRebuildOffer: "That's a bigger shift. If you'd rather restart this week around it, you can rebuild — that replaces the remaining meals and changes your shopping list.",
        bodyRebuildCta: "Rebuild this week",
        dietaryStyleLabel: "Dietary style",
        dietaryStyleHint: "A hard rule — your plans never cross it. Changing it rebuilds your plan.",
        timeBudgetLabel: "Cooking time",
        timeBudgetHint: "How long a normal weekday meal may take. Changing it rebuilds your plan.",
    },
    goals: {
        ownTargetCta: "Set my own target",
        ownTargetLabel: "Your daily target (kcal)",
        ownTargetBody: "Your choice, honestly handled: plans will be built to this number instead of the calculated one. You can switch back anytime.",
        ownTargetSave: "Use this target",
        ownTargetCancel: "Cancel",
        ownTargetRestore: "Back to the calculated target",
        floorDisclaimerTitle: "This is below your safety floor",
        floorDisclaimerBody: "The floor exists to protect you — while breastfeeding it also protects your milk supply. If you continue, you confirm you choose this target on your own responsibility and will discuss it with your doctor, midwife or a dietitian.",
        floorDisclaimerAck: "I understand and take responsibility for this target.",
        ownTargetError: "We couldn't save that target. Please try again.",
        title: "Goals",
        back: "Back to Settings",
        goalLabel: "Your goal",
        targetTitle: "Daily calorie target",
        computedNote: "Computed with the Mifflin-St Jeor formula from your body data and goal — not a promise, a planning target.",
        manualNote: "Set manually by you. Plans are validated against this target.",
        toleranceLabel: "Plans stay within ±{{percent}}% of this target.",
        macrosTitle: "Daily macros",
        macrosHint: "How your daily calories split into protein, carbs and fat. We work these out automatically from your calorie target and goal — no need to set them yourself.",
        proteinLabel: "Protein",
        carbsLabel: "Carbs",
        fatLabel: "Fat",
        sources: {
            title: "Sources & method",
            eyebrow: "How BiteBalance calculates",
            linkLabel: "Sources & method",
            intro: "Your calorie target is a planning target, not a medical prescription. These are the published sources behind every number BiteBalance states about energy and nutrients. Talk to your doctor or a dietitian before changing how you eat for a health condition, in pregnancy or while breastfeeding.",
            openHint: "Links open in your browser.",
            groups: {
                target: {
                    title: "Daily calorie target",
                    body: "Resting energy (basal metabolic rate) is calculated with the Mifflin-St Jeor equation from your sex, age, height and weight. Your daily movement and declared sport are added on top (sport from MET values in the Compendium of Physical Activities, or your wearable's active-energy average if you entered one). The result is then adjusted for your goal: −15% for weight loss, +10% for weight gain, unchanged for maintenance and health. Plans stay within ±10% of the target.",
                },
                macros: {
                    title: "Protein, carbs and fat",
                    body: "Your calories are split 25% protein, 45% carbohydrates and 30% fat (protein and carbohydrates at 4 kcal/g, fat at 9 kcal/g). This split lies inside the Acceptable Macronutrient Distribution Ranges of the US Institute of Medicine and the DGE reference values.",
                },
                floor: {
                    title: "Safety minimums",
                    body: "BiteBalance never suggests or accepts a target below your resting energy, nor below 1,200 kcal/day for women or 1,500 kcal/day for men. For comparison, clinical guidance places medically supervised low-calorie diets at 1,000–1,200 kcal/day for most women and 1,200–1,600 kcal/day for men; BiteBalance's minimums sit above the lower end of that band and are a guard rail, not a recommendation.",
                },
                breastfeeding: {
                    title: "Breastfeeding",
                    body: "If you told us you are breastfeeding, 330 kcal/day are added to your energy needs for every goal, a weight-loss deficit is capped to roughly 0.5 kg per week, and the minimum rises to 1,800 kcal/day. Published recommendations put the additional need at 330–400 kcal/day (CDC, US Institute of Medicine) or +500 kcal/day during exclusive breastfeeding (DGE).",
                },
            },
            items: {
                mifflin: {
                    title: "Mifflin MD, St Jeor ST et al. (1990)",
                    detail: "A new predictive equation for resting energy expenditure in healthy individuals. American Journal of Clinical Nutrition 51(2), 241–247.",
                },
                compendium: {
                    title: "Ainsworth BE et al. (2011)",
                    detail: "2011 Compendium of Physical Activities: a second update of codes and MET values. Medicine & Science in Sports & Exercise 43(8), 1575–1581.",
                },
                iom: {
                    title: "US Institute of Medicine (2005)",
                    detail: "Dietary Reference Intakes for Energy, Carbohydrate, Fiber, Fat, Fatty Acids, Cholesterol, Protein, and Amino Acids — macronutrient ranges and energy in lactation.",
                },
                dge: {
                    title: "Deutsche Gesellschaft für Ernährung (DGE)",
                    detail: "Reference values for nutrient intake: energy — including the guideline value for breastfeeding women.",
                },
                nhlbi: {
                    title: "NHLBI / NAASO (2000)",
                    detail: "The Practical Guide: Identification, Evaluation, and Treatment of Overweight and Obesity in Adults (PDF) — low-calorie diet ranges and the 0.5–1 kg/week pace.",
                },
                cdc: {
                    title: "US Centers for Disease Control and Prevention",
                    detail: "Maternal Diet and Breastfeeding — the additional 330–400 kcal/day for well-nourished breastfeeding mothers.",
                },
            },
        },
        bodyTitle: "Body data",
        currentWeightLabel: "Current weight",
        targetWeightLabel: "Target weight",
        notProvided: "Not provided",
        bodyEditHint: "Body data comes from your onboarding answers. Changing it recomputes your targets.",
        timeBudgetTitle: "Time budget",
        perMealLabel: "Per meal",
        perDayLabel: "Per day",
        dietaryStyleTitle: "Dietary style",
        dietaryStyleEditLink: "Edit your dietary style under Preferences",
        saveButton: "Save goal",
        saved: "Saved.",
        saveError: "We couldn't save that. Please try again.",
        regenTitle: "Goal saved",
        regenBody: "Your current plan keeps its meals. The new goal applies the next time your plan is generated.",
        regenCta: "Regenerate plan now",
        regenStarted: "Plan regeneration started. Your new plan will appear shortly.",
        regenError: "We couldn't start the regeneration. Please try again.",
    },
    constraints: {
        title: "Food & health",
        back: "Back to Settings",
        allergensTitle: "What you don't eat",
        allergySectionTitle: "Allergies & intolerances",
        avoidSectionTitle: "Foods I don't eat",
        noneChip: "None",
        healthTitle: "Health conditions",
        editButton: "Edit",
        dialogTitle: "Confirm this change",
        dialogBody: "Changing a hard rule regenerates your current plan so it stays safe. This can't be undone automatically.",
        dialogConfirmLabel: "I understand this will regenerate my plan.",
        confirmButton: "Confirm change",
        cancelButton: "Cancel",
        updated: "Updated. Your plan will be regenerated to reflect this.",
        updateError: "We couldn't save that change. Please try again.",
        hardPill: "Hard",
        hardNote: "Hard limits are never bypassed, softened or learned away.",
        addButton: "Add",
        // ADR-080: intolerances shown and edited next to allergies; the one-time question for legacy "Milk / lactose" rows.
        intoleranceSectionTitle: "Intolerances",
        intoleranceLactose: "Lactose intolerance",
        levelAvoidAll: "avoids all dairy",
        levelFreeOk: "lactose-free dairy is fine",
        intoleranceTextLabel: "Other intolerances",
        clarifyMilkTitle: "One question about milk",
        clarifyMilkBody: "You selected \"Milk / lactose\" earlier. Is that a milk allergy or a lactose intolerance? Until you answer, we keep treating it as a milk allergy — the safer reading.",
        clarifyMilkAllergy: "It's a milk allergy",
        clarifyMilkIntolerance: "It's a lactose intolerance",
        clarifyLevelTitle: "How strict is your lactose intolerance?",
        clarifyLevelBody: "You told us about a lactose intolerance before we asked how strict it is. Until you choose, all dairy stays out.",
        clarifyConfirm: "Save my answer",
        // ADR-080: the four refusals a Food & health save can get — said in the user's words
        errorLevelRequired: "Choose how strict your lactose intolerance is before saving.",
        errorLevelNotAllowed: "A strictness level only applies to the lactose intolerance option.",
        errorClarificationUnresolved: "Pick one answer to the milk question before saving.",
    },
    memory: {
        title: "Memory",
        back: "Back to Settings",
        loadError: "We couldn't load your memory. Please try again.",
        tastesTitle: "Tastes Amy knows",
        tastesDislikes: "Avoids for you",
        tastesLikes: "You love",
        tastesEmpty: "No saved tastes yet — tell Amy in chat (\"I don't like bacon\") and it lands here.",
        tastesHint: "Saved tastes steer every future plan. Edit them under Preferences.",
        pendingTitle: "Waiting for your confirmation",
        pendingEmpty: "Nothing pending. Amy will ask before remembering anything new.",
        firstRunTitle: "Amy hasn't saved anything yet",
        firstRunBody: "When you tell Amy things worth remembering — tastes, habits, life context — they appear here for YOUR approval first. Nothing is ever saved behind your back.",
        firstRunCta: "Talk to Amy",
        confirmButton: "Remember this",
        rejectButton: "No, don't remember this",
        confirmError: "We couldn't save that. Please try again.",
        rejectError: "We couldn't dismiss that. Please try again.",
        confirmedTitle: "What Amy remembers",
        confirmedEmpty: "Nothing remembered yet. Confirm a suggestion from Amy to see it here.",
        originLabel: "Origin",
        originAmyChat: "From a conversation with Amy",
        effectLabel: "Effect on your plans",
        effectIngredientDislike: "This ingredient is demoted in future weekly plans.",
        effectIngredientLike: "This ingredient is favored in future weekly plans.",
        effectRecipeDislike: "This recipe is demoted in future weekly plans.",
        effectRecipeLike: "This recipe is favored in future weekly plans.",
        effectCuisine: "This cuisine is favored in future weekly plans.",
        effectNote: "Colors Amy's conversation only — it never changes your plan or shopping list.",
        effectOther: "Saved for reference; it does not yet change plan generation.",
        deleteButton: "Delete",
        deleteConfirmTitle: "Delete this memory?",
        deleteConfirmBody: "Amy will no longer take this into account when planning your meals, and won't bring it up again unless you tell her.",
        deleteConfirmButton: "Delete",
        cancelButton: "Cancel",
        deleteError: "We couldn't delete that. Please try again.",
    },
};
